import { INDICATOR } from '@/api/rest/resources/types/IndicatorProduct';
import { Logger } from '@/lib/logs/logger';
import { resolveThemeColor } from '@/utils/resolveThemeColor';

export const INDICATORS_COLORS_MAP: Partial<{
  [key in (typeof INDICATOR)[keyof typeof INDICATOR]]: string;
}> = {
  [INDICATOR.ABOVE_GROUND_CARBON]: 'indicator-above-ground-carbon-',
  [INDICATOR.CARBON]: 'indicator-below-ground-carbon-',
  [INDICATOR.COOLFARMTOOL_CO2_SPATIALISE]: 'indicator-below-ground-carbon-',
  [INDICATOR.PROTECTED_ON_FARM_HABITAT]: 'indicator-biodiversity-',
  [INDICATOR.WATER_LOW_RESOLUTION]: 'indicator-water-holding-capacity-',
  [INDICATOR.WATER_HIGH_RESOLUTION]: 'indicator-water-holding-capacity-',
  [INDICATOR.SOIL_MOISTURE]: 'indicator-soil-moisture-',
  [INDICATOR.EO_VEGETATION_CONDITION]: 'indicator-vegetation-condition-',
  [INDICATOR.SOCIAL_LIFE_EXPECTANCY]: 'indicator-life-expectancy-',
  [INDICATOR.CANOPY_COVER]: 'indicator-canopy-cover-',
  [INDICATOR.CANOPY_HEIGHT]: 'indicator-canopy-height-',
  [INDICATOR.LANDSCAPE_PROTECTED_AREAS_OSM]: 'indicator-protected-areas-',
  [INDICATOR.LANDSCAPE_PROTECTED_AREAS_WDPA]: 'indicator-protected-areas-',
  [INDICATOR.PRECIPITATION]: 'indicator-precipitation-',
  [INDICATOR.HABITAT_INTACTNESS]: 'indicator-habitat-intactness-',
  [INDICATOR.HABITAT_INTACTNESS_THREATS_BREAKDOWN]: 'indicator-habitat-intactness-threats-breakdown-',
  [INDICATOR.POPULATION_DENSITY]: 'indicator-population-density-',
} as const;

export const getColorForIndicator = (
  indicator: (typeof INDICATOR)[keyof typeof INDICATOR],
): {
  stroke: string;
  fill: string;
  confidenceInterval: string;
  estimatedData: string;
} => {
  const getColors = (indicatorArg: (typeof INDICATOR)[keyof typeof INDICATOR]) => {
    const colorPrefix = INDICATORS_COLORS_MAP[`${indicatorArg}`];
    return {
      stroke: resolveThemeColor(`${colorPrefix}stroke`) ?? '',
      fill: resolveThemeColor(`${colorPrefix}fill`) ?? '',
      confidenceInterval: resolveThemeColor(`${colorPrefix}confidence-interval`) ?? '',
      estimatedData: resolveThemeColor(`${colorPrefix}estimated-data`) ?? '',
    };
  };

  switch (indicator) {
    case INDICATOR.ABOVE_GROUND_CARBON:
      return getColors(INDICATOR.ABOVE_GROUND_CARBON);
    case INDICATOR.CARBON:
    case INDICATOR.COOLFARMTOOL_CO2_SPATIALISE:
      return getColors(INDICATOR.CARBON);
    case INDICATOR.PROTECTED_ON_FARM_HABITAT:
      return getColors(INDICATOR.PROTECTED_ON_FARM_HABITAT);
    case INDICATOR.WATER_HIGH_RESOLUTION:
      return getColors(INDICATOR.WATER_HIGH_RESOLUTION);
    case INDICATOR.WATER_LOW_RESOLUTION:
      return getColors(INDICATOR.WATER_LOW_RESOLUTION);
    case INDICATOR.SOIL_MOISTURE:
      return getColors(INDICATOR.SOIL_MOISTURE);
    case INDICATOR.EO_VEGETATION_CONDITION:
      return getColors(INDICATOR.EO_VEGETATION_CONDITION);
    case INDICATOR.SOCIAL_LIFE_EXPECTANCY:
      return getColors(INDICATOR.SOCIAL_LIFE_EXPECTANCY);
    case INDICATOR.CANOPY_COVER:
      return getColors(INDICATOR.CANOPY_COVER);
    case INDICATOR.CANOPY_HEIGHT:
      return getColors(INDICATOR.CANOPY_HEIGHT);
    case INDICATOR.LANDSCAPE_PROTECTED_AREAS_OSM:
    case INDICATOR.LANDSCAPE_PROTECTED_AREAS_WDPA:
      return getColors(INDICATOR.LANDSCAPE_PROTECTED_AREAS_OSM);
    case INDICATOR.PRECIPITATION:
      return getColors(INDICATOR.PRECIPITATION);
    case INDICATOR.HABITAT_INTACTNESS:
      return getColors(INDICATOR.HABITAT_INTACTNESS);
    case INDICATOR.HABITAT_INTACTNESS_THREATS_BREAKDOWN:
      return getColors(INDICATOR.HABITAT_INTACTNESS_THREATS_BREAKDOWN);
    case INDICATOR.POPULATION_DENSITY:
      return getColors(INDICATOR.POPULATION_DENSITY);
    default:
      Logger.error(`No color defined for indicator "${indicator}"`);
      return {
        fill: resolveThemeColor('neutral-black-80'),
        stroke: resolveThemeColor('neutral-black-40'),
        confidenceInterval: '',
        estimatedData: '',
      };
  }
};
